/**
 * Utilitaires de formatage de dates
 * Fonctions pures < 30 lignes chacune
 */

/**
 * Formate une date courte (DD/MM/YYYY)
 * @param {string|Date} date - Date à formater
 * @returns {string} - Date formatée
 */
export function formatDate(date) {
  if (!date) return ''
  const dateObj = date instanceof Date ? date : new Date(date)
  if (isNaN(dateObj.getTime())) return ''
  
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(dateObj)
}

/**
 * Une date lue TELLE QU'ELLE A ÉTÉ POSÉE, en UTC.
 *
 * POURQUOI CE SECOND FORMATEUR. Une échéance de fin de journée s'écrit
 * `23:59:59.999Z` : en heure de Paris, c'est déjà le lendemain, et `formatDate`
 * affichait donc « 01/01/2028 » pour un contrat qui expire le 31 décembre 2027.
 * Le décalage se voyait partout à la fois — page de signature, fiche du
 * back-office, historique — parce qu'il vient de la lecture, pas de la donnée.
 *
 * À RÉSERVER aux dates qui désignent un JOUR (une échéance, une validité) et
 * non un instant : pour « signé à 14 h 32 », c'est bien l'heure locale du
 * lecteur qu'il faut, donc `formatDate`.
 *
 * @param {string|Date} date
 * @returns {string} - "DD/MM/YYYY", ou '' si la date est absente ou invalide
 */
export function formatDateUTC(date) {
  if (!date) return ''
  const dateObj = date instanceof Date ? date : new Date(date)
  if (isNaN(dateObj.getTime())) return ''

  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC'
  }).format(dateObj)
}

/**
 * Date du jour au format ISO court "YYYY-MM-DD".
 * Usage : borner un <input type="date"> (attribut `max`) pour interdire le futur.
 * @returns {string} - Date du jour "YYYY-MM-DD"
 */
export function todayISODate() {
  return new Date().toISOString().slice(0, 10)
}

/**
 * Convertit une date (ISO renvoyée par l'API, "YYYY-MM-DD" ou vide/null) vers la
 * valeur attendue par un <input type="date"> : "YYYY-MM-DD", ou '' si absente.
 * @param {string|Date|null|undefined} value
 * @returns {string} - "YYYY-MM-DD" ou ''
 */
export function toDateInputValue(value) {
  return value ? String(value).slice(0, 10) : ''
}

/**
 * Convertit une date (Date, ISO UTC de l'API…) vers la valeur attendue par un
 * <input type="datetime-local"> : "YYYY-MM-DDTHH:mm" en HEURE LOCALE.
 * Injecter l'ISO UTC brut décalerait l'heure à chaque enregistrement.
 * @param {string|Date|null|undefined} value
 * @returns {string} - "YYYY-MM-DDTHH:mm" ou '' si invalide/absente
 */
export function toDatetimeLocalValue(value) {
  const date = value instanceof Date ? value : new Date(value ?? NaN)
  if (isNaN(date.getTime())) return ''
  const pad = (n) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/**
 * Formate une date longue (1 janvier 2024)
 * @param {string|Date} date - Date à formater
 * @returns {string} - Date formatée
 */
export function formatDateLong(date) {
  if (!date) return ''
  const dateObj = date instanceof Date ? date : new Date(date)
  if (isNaN(dateObj.getTime())) return ''
  
  return new Intl.DateTimeFormat('fr-FR', {
    dateStyle: 'long'
  }).format(dateObj)
}

/**
 * Formate une date avec heure (DD/MM/YYYY HH:mm)
 * @param {string|Date} date - Date à formater
 * @returns {string} - Date formatée
 */
/**
 * Une PLAGE de jours, écrite comme on la dit : « 30 août 2026 »,
 * « du 25 au 27 août 2026 », « du 30 août au 2 septembre 2026 ».
 *
 * POURQUOI PAS `formatSessionDates` (interSessionsUtils) : celui-ci écrit
 * toujours « Du 25/08/2026 au 27/08/2026 », y compris quand la formation tient
 * en une journée — le lecteur y voit deux dates là où il n'y en a qu'une, et
 * le mois en chiffres se déchiffre au lieu de se lire. Il reste en place pour
 * les écrans du back-office qui l'utilisent ; celui-ci sert les portails, où
 * le lecteur n'est pas un professionnel de l'outil.
 *
 * La répétition est retirée par la DROITE : on ne répète l'année que si elle
 * change, ni le mois que si le mois change.
 *
 * @param {string|Date} debut
 * @param {string|Date} [fin] Absente ou égale au début = une seule journée.
 * @param {string} [siVide] Ce qu'on écrit quand il n'y a pas de date du tout.
 * @returns {string}
 */
export function formatPlageDatesLongue(debut, fin, siVide = 'Dates à confirmer') {
  if (!debut) return siVide

  const d = debut instanceof Date ? debut : new Date(debut)
  if (isNaN(d.getTime())) return siVide

  const f = fin ? (fin instanceof Date ? fin : new Date(fin)) : d
  const finValide = !isNaN(f.getTime()) ? f : d

  // Les dates de session sont des colonnes `@db.Date` : minuit UTC. Les lire
  // dans le fuseau du navigateur reculerait le jour d'un cran à l'ouest de
  // Greenwich — la formation du 30 s'afficherait « 29 ».
  const jour = (x) => x.getUTCDate()
  const mois = (x) => x.getUTCMonth()
  const annee = (x) => x.getUTCFullYear()

  const nomMois = (x) =>
    new Intl.DateTimeFormat('fr-FR', { month: 'long', timeZone: 'UTC' }).format(x)

  const jourComplet = (x) => `${jour(x)} ${nomMois(x)} ${annee(x)}`

  const memeJour =
    jour(d) === jour(finValide) && mois(d) === mois(finValide) && annee(d) === annee(finValide)
  if (memeJour) return jourComplet(d)

  if (annee(d) !== annee(finValide)) {
    return `du ${jourComplet(d)} au ${jourComplet(finValide)}`
  }
  if (mois(d) !== mois(finValide)) {
    return `du ${jour(d)} ${nomMois(d)} au ${jourComplet(finValide)}`
  }
  return `du ${jour(d)} au ${jourComplet(finValide)}`
}

export function formatDateTime(date) {
  if (!date) return ''
  const dateObj = date instanceof Date ? date : new Date(date)
  if (isNaN(dateObj.getTime())) return ''
  
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(dateObj)
}

/**
 * Alias de formatDateTime pour compatibilité
 * @param {string|Date} date - Date à formater
 * @returns {string} - Date formatée
 */
export function formatDateWithTime(date) {
  return formatDateTime(date)
}

/**
 * Formate une date sans heure (DD/MM/YYYY)
 * @param {string|Date} date - Date à formater
 * @returns {string} - Date formatée
 */
export function formatDateOnly(date) {
  return formatDate(date)
}

/**
 * Formate une date courte (DD/MM/YYYY) - alias
 * @param {string|Date} date - Date à formater
 * @returns {string} - Date formatée
 */
export function formatDateShort(date) {
  return formatDate(date)
}

/**
 * Formate une date pour affichage dans un tableau (DD/MM/YYYY)
 * @param {string|Date} date - Date à formater
 * @returns {string} - Date formatée
 */
export function formatDateTable(date) {
  return formatDate(date)
}

/**
 * Formate une date au format "DD/MM/YYYY à HH:mm" en heure locale.
 * Le passage heure d'été / heure d'hiver est géré automatiquement par
 * Intl.DateTimeFormat (pas de décalage codé en dur).
 * @param {string|Date} date - La date à formater (ISO UTC ou Date)
 * @returns {string} La date formatée
 */
export function formatDateTimeFr(date) {
  if (!date) return ''
  const dateObj = date instanceof Date ? date : new Date(date)
  if (isNaN(dateObj.getTime())) return ''

  const parts = new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).formatToParts(dateObj)
  const get = (type) => parts.find(p => p.type === type)?.value || ''

  return `${get('day')}/${get('month')}/${get('year')} à ${get('hour')}:${get('minute')}`
}

/**
 * Formate une date avec heure et année courte (DD/MM/YY HH:mm)
 * @param {string|Date} date - Date à formater
 * @returns {string} - Date formatée
 */
export function formatDateTimeShort(date) {
  if (!date) return ''
  const dateObj = date instanceof Date ? date : new Date(date)
  if (isNaN(dateObj.getTime())) return ''

  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }).format(dateObj)
}

/**
 * Formate une date au format YYYY-MM-DD pour stockage
 * @param {string|Date} date - Date à formater
 * @returns {string} - Date formatée (YYYY-MM-DD)
 */
export function formatDateForStorage(date) {
  if (!date) return ''
  
  const dateObj = date instanceof Date ? date : new Date(date)
  if (isNaN(dateObj.getTime())) return ''
  
  const year = dateObj.getFullYear()
  const month = String(dateObj.getMonth() + 1).padStart(2, '0')
  const day = String(dateObj.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Formate une date pour affichage dans un calendrier (jour mois année)
 * @param {string|Date} dateStr - Date à formater
 * @returns {string} - Date formatée
 */
export function formatDateForDisplay(dateStr) {
  if (!dateStr) return ''
  
  if (dateStr instanceof Date) {
    const year = dateStr.getFullYear()
    const month = String(dateStr.getMonth() + 1).padStart(2, '0')
    const day = String(dateStr.getDate()).padStart(2, '0')
    dateStr = `${year}-${month}-${day}`
  }
  
  if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split('-')
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })
  }
  
  try {
    const date = new Date(dateStr + 'T00:00:00')
    if (isNaN(date.getTime())) {
      return dateStr
    }
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })
  } catch (e) {
    return dateStr
  }
}

/**
 * Convertit une date au format YYYY-MM-DD
 * @param {string|Date} date - Date à convertir
 * @returns {string} - Date au format YYYY-MM-DD
 */
export function formatDateToYYYYMMDD(date) {
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date
  }
  if (date instanceof Date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }
  const dateObj = new Date(date)
  if (!isNaN(dateObj.getTime())) {
    const year = dateObj.getFullYear()
    const month = String(dateObj.getMonth() + 1).padStart(2, '0')
    const day = String(dateObj.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }
  return date
}

/**
 * Formate une date courte avec mois abrege (25 fevr. 2026)
 * @param {string|Date} date - Date a formater
 * @returns {string} - Date formatee
 */
export function formatDateShortMonth(date) {
  if (!date) return '-'
  const dateObj = date instanceof Date ? date : new Date(date)
  if (isNaN(dateObj.getTime())) return '-'

  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(dateObj)
}

/**
 * Formate une date complète avec jour de la semaine (lundi 1 janvier 2024 à 14:30)
 * @param {string|Date} dateStr - Date à formater
 * @returns {string} - Date formatée
 */
/**
 * Formate un tableau de dates au format YYYY-MM-DD
 * @param {Array<string|Date>} dates - Tableau de dates
 * @returns {Array<string>} - Dates formatées
 */
export function formatDatesArray(dates) {
  return dates.map(date => formatDateToYYYYMMDD(date))
}

/**
 * Formate une date en temps relatif (il y a X minutes/heures/jours)
 * @param {string|Date} dateStr - Date à formater
 * @returns {string} - Temps relatif en français
 */
export function formatRelativeTime(dateStr) {
  const now = new Date()
  const date = new Date(dateStr)
  const diffMs = now - date
  const diffMinutes = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMinutes < 1) return "à l'instant"
  if (diffMinutes < 60) return `il y a ${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`
  if (diffHours < 24) return `il y a ${diffHours} heure${diffHours > 1 ? 's' : ''}`
  if (diffDays < 7) return `il y a ${diffDays} jour${diffDays > 1 ? 's' : ''}`
  return date.toLocaleDateString('fr-FR')
}

export function formatFullDate(dateStr) {
  if (!dateStr) return ''
  
  let date
  if (dateStr instanceof Date) {
    date = dateStr
  } else if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    date = new Date(dateStr)
  } else if (typeof dateStr === 'string') {
    const parts = dateStr.split('/')
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10)
      const month = parseInt(parts[1], 10) - 1
      const year = parseInt(parts[2], 10)
      date = new Date(year, month, day)
    } else {
      date = new Date(dateStr)
    }
  } else {
    date = new Date(dateStr)
  }
  
  if (isNaN(date.getTime())) return String(dateStr)
  
  return date.toLocaleDateString('fr-FR', { 
    weekday: 'long', 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}
